import {
  FunctionComponent,
  useState,
  useRef,
  useContext,
  useEffect,
  ReactNode,
} from 'react'
import styled, { css, useTheme } from 'styled-components'
import contextToiTeaser from '@contexts/toiTeaser'
import contextViewport from '@contexts/viewport'
import { ComputedTeaserFlex } from '@widgets/Teaser/types'
import { ViewportTypeValues } from '@contexts/viewport'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import SvgIcon from '@components/SvgIcon'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

const { TOITeaserLayoutContext, TOITeaserSportsTilesContext } = contextToiTeaser
const { ViewportTypeContext } = contextViewport

const tilesShownOnDesktop = 4

const teaserWidthDesktopFlex6 = computeTeaserWidth({
  flexValue: '6',
  viewportType: ViewportTypeValues.DESKTOP,
})

/* +1 => Design-Workaround: On Desktop, the last visible item will not end with a white border */
const nonMobileTileWidth = `${
  teaserWidthDesktopFlex6 / tilesShownOnDesktop + 1
}px`

const mobileTileWidth = '210px'

const StyledSportsTiles = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_100 },
      },
    },
  }) => css`
    margin-top: 2px;
    width: 100%;
    background-color: ${grey_100};
    position: relative;
  `}
`

const StyledTilesWrapper = styled.div`
  ${() => css`
    display: grid;
    grid-auto-flow: column;
    height: 80px;

    ${desktopCSS(css`
      grid-auto-columns: ${nonMobileTileWidth};
    `)}

    ${tabletCSS(css`
      grid-auto-columns: ${nonMobileTileWidth};
    `)}

    ${mobileCSS(css`
      grid-auto-columns: ${mobileTileWidth};
    `)}
  `}
`

const StyledSportsTilesInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledScroll = styled.button<{
  teaserFlex: ComputedTeaserFlex
  scrollDirection: 'left' | 'right'
  hide: boolean
}>`
  ${({ teaserFlex, scrollDirection, hide }) => {
    const teaserFlexDesktop = teaserFlex['desktop']

    return css`
      ${desktopCSS(css`
        ${!hide && teaserFlexDesktop === '6'
          ? css`
        position: absolute;
        height: 100%;
        top: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background-color: transparent;
        border: 0;
        padding: 0;
        cursor: pointer;
        padding-${scrollDirection === 'left' ? 'right' : 'left'}: 20px;
        ${scrollDirection}: 0;
        `
          : css`
              display: none;
            `};
      `)}

      ${tabletCSS(css`
        display: none;
      `)}

      ${mobileCSS(css`
        display: none;
      `)}
    `
  }};
`

const StyledSvgIcon = styled(SvgIcon)`
  ${({
    theme: {
      colors: { white },
    },
  }) => css`
    display: block;
    background-color: ${white};
  `}
`

const SportsTiles: FunctionComponent<{ children?: ReactNode }> = ({
  children,
}) => {
  const theme = useTheme()
  const [position, setPosition] = useState<number>(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const viewportType = useContext(ViewportTypeContext)
  const { teaserFlex } = useContext(TOITeaserLayoutContext)
  const sportsTilesNumber = useContext(TOITeaserSportsTilesContext)
  const sportsTilesViewportArea = computeTeaserWidth({
    flexValue: teaserFlex[viewportType],
    viewportType:
      viewportType === ViewportTypeValues.DESKTOP
        ? ViewportTypeValues.DESKTOP
        : ViewportTypeValues.TABLET,
  })
  const tileWidth = sportsTilesViewportArea / 4

  useEffect(() => {
    setPosition(0)
  }, [sportsTilesNumber])

  useEffect(() => {
    if (scrollContainerRef.current) {
      if (position === sportsTilesNumber - 1) {
        scrollContainerRef.current.scrollLeft =
          position * tileWidth - (sportsTilesViewportArea - tileWidth)
      } else {
        scrollContainerRef.current.scrollLeft = position * tileWidth
      }
    }
  }, [position, sportsTilesViewportArea, sportsTilesNumber, tileWidth])

  return (
    <StyledSportsTiles>
      <StyledSportsTilesInnerWrapper ref={scrollContainerRef}>
        <StyledTilesWrapper>{children}</StyledTilesWrapper>
        <StyledScroll
          aria-label="scroll left"
          scrollDirection="left"
          teaserFlex={teaserFlex}
          hide={sportsTilesNumber < 5 || position === 0}
          onClick={(e) => {
            e.stopPropagation()
            if (position > 0) {
              setPosition((currentPosition) => currentPosition - 1)
            }
          }}>
          <StyledSvgIcon
            iconName="gallery-previous"
            size={24}
            color={theme.colors.grey.grey_100}
          />
        </StyledScroll>
        <StyledScroll
          aria-label="scroll right"
          scrollDirection="right"
          teaserFlex={teaserFlex}
          hide={sportsTilesNumber < 5 || position === sportsTilesNumber - 4}
          onClick={(e) => {
            e.stopPropagation()
            if (position < sportsTilesNumber - 4) {
              setPosition((currentPosition) => currentPosition + 1)
            }
          }}>
          <StyledSvgIcon
            iconName="gallery-next"
            size={24}
            color={theme.colors.grey.grey_100}
          />
        </StyledScroll>
      </StyledSportsTilesInnerWrapper>
    </StyledSportsTiles>
  )
}

export default SportsTiles
